import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["spinner", "text"]
  
  // Values that can be configured with data attributes
  static values = {
    loadingText: String,
    variant: { type: String, default: "primary" },
    delay: { type: Number, default: 300 },
    newTab: Boolean
  }
  
  initialize() {
    // Find the closest form if the controller is within one
    this.form = this.element.closest("form")
    if (this.form) {
      this.form.addEventListener("turbo:submit-start", this.startLoading.bind(this))
      this.form.addEventListener("turbo:submit-end", this.stopLoading.bind(this))
    }
  }
  
  disconnect() {
    if (this.form) {
      this.form.removeEventListener("turbo:submit-start", this.startLoading.bind(this))
      this.form.removeEventListener("turbo:submit-end", this.stopLoading.bind(this))
    }
  }
  
  connect() {
    this.isLoading = false
    this.originalText = this.textTarget.textContent
    
    // Use the value from data attribute or fallback to default
    this.loadingText = this.hasLoadingTextValue ? this.loadingTextValue : "Loading..."
    
    // Apply variant styling if specified
    if (this.hasVariantValue) {
      this.applyVariant()
    }
  }
  
  applyVariant() {
    const variant = this.variantValue
    
    // Remove any existing variant classes
    this.element.classList.remove(
      "bg-blue-600",
      "hover:bg-blue-700",
      "bg-gray-600",
      "hover:bg-gray-700",
      "bg-red-600",
      "hover:bg-red-700"
    )
    
    // Apply new variant classes
    switch (variant) {
      case "primary":
        this.element.classList.add("bg-blue-600", "hover:bg-blue-700")
        this.updateSpinnerColor("border-white")
        break
      case "secondary":
        this.element.classList.add("bg-gray-600", "hover:bg-gray-700")
        this.updateSpinnerColor("border-white")
        break
      case "danger":
        this.element.classList.add("bg-red-600", "hover:bg-red-700")
        this.updateSpinnerColor("border-white")
        break
      default:
        this.element.classList.add("bg-blue-600", "hover:bg-blue-700")
        this.updateSpinnerColor("border-white")
    }
  }
  
  updateSpinnerColor(colorClass) {
    // Remove any existing border color classes
    this.spinnerTarget.firstElementChild.classList.remove(
      "border-white", 
      "border-gray-800"
    )
    
    // Add the new color class
    this.spinnerTarget.firstElementChild.classList.add(colorClass)
  }
  
  async startLoading() {
    this.element.disabled = true
    
    // Start spinner animation
    this.spinnerTarget.classList.remove("hidden")
    
    // Fade out current text
    this.textTarget.classList.add("opacity-0")
    
    // Wait for fade out transition
    await new Promise(resolve => setTimeout(resolve, 200))
    
    // Update text and fade in
    if (this.hasLoadingTextValue) {
      this.originalText = this.textTarget.textContent
      this.textTarget.textContent = this.loadingTextValue
    }
    this.textTarget.classList.remove("opacity-0")
  }
  
  async stopLoading() {
    // Fade out loading text
    this.textTarget.classList.add("opacity-0")
    
    // Wait for fade out transition
    await new Promise(resolve => setTimeout(resolve, 200))
    
    // Update text and fade in
    if (this.hasLoadingTextValue && this.originalText) {
      this.textTarget.textContent = this.originalText
    }
    this.textTarget.classList.remove("opacity-0")
    
    // Hide spinner and enable button
    this.spinnerTarget.classList.add("hidden")
    this.element.disabled = false
  }
}